"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { api } from "@/lib/api";
import type { Source, Target, RouteItem } from "@/lib/types/routing";
import type { TargetStatusChangedEvent } from "@/lib/types/signalr";
import { useSignalR } from "@/lib/hooks/use-signalr";
import { FlowCanvas } from "@/components/flow/flow-canvas";

export function FlowMapTab() {
  const t = useTranslations();
  const [sources, setSources] = useState<Source[]>([]);
  const [targets, setTargets] = useState<Target[]>([]);
  const [routes, setRoutes] = useState<RouteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.sources.list(),
      api.targets.list(),
      api.routes.list(),
    ])
      .then(([s, tg, r]) => {
        setSources(s);
        setTargets(tg);
        setRoutes(r);
      })
      .catch(() => setError(t("dashboard.flow.loadError")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- next-intl t is stable across renders
  }, []);

  // Real-time: update target node online/offline dot
  useSignalR("TargetStatusChanged", (event: unknown) => {
    const e = event as TargetStatusChangedEvent;
    setTargets((prev) =>
      prev.map((t) =>
        t.id === e.targetId
          ? { ...t, status: e.status, balance: e.balance ?? t.balance, equity: e.equity ?? t.equity }
          : t
      )
    );
  });

  return (
    <div className="flex h-[calc(100vh-16rem)] min-h-[420px] flex-col overflow-hidden rounded-xl border border-border">
      <div className="border-b border-border px-4 py-3">
        <p className="text-sm text-muted-foreground">{t("dashboard.flow.subtitle")}</p>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {loading && (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-muted-foreground">{t("dashboard.flow.loading")}</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {!loading && !error && sources.length === 0 && targets.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-2">
            <p className="text-sm font-medium">{t("dashboard.flow.emptyTitle")}</p>
            <p className="text-xs text-muted-foreground">{t("dashboard.flow.emptyBody")}</p>
          </div>
        )}

        {!loading && !error && (sources.length > 0 || targets.length > 0) && (
          <FlowCanvas
            sources={sources}
            targets={targets}
            initialRoutes={routes}
          />
        )}
      </div>
    </div>
  );
}
